import {
  FaEye,
  FaInfoCircle,
} from "react-icons/fa";
import CheckoutItemTable from "./CheckoutItemTable";
import { FiCalendar, FiClock } from "react-icons/fi";
import { FaUserTie } from "react-icons/fa";
import { useEffect, useState, useRef } from "react";
import { httpService } from "../../services/httpService";
import { toast } from "react-toastify";

// ---------- Helper: format time ----------
const format12Hour = (timeStr) => {
  if (!timeStr) return "";
  if (/(am|pm)/i.test(timeStr)) return timeStr.trim();
  const parts = timeStr.split(":");
  const h = parseInt(parts[0], 10);
  const m = parts[1] || "00";
  const period = h >= 12 ? "pm" : "am";
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
};

const GroomingCheckoutCard = ({
  service,
  pet,
  getPetIcon,
  onUpdate,
  onCancel,
  clientId,
  onViewActivity,
  isOrderClosed,
}) => {
  // ---------- Local state ----------
  const [serviceId, setServiceId] = useState(service.service_id || "");
  const [staffId, setStaffId] = useState(service.technician_id || "");
  const [selectedTime, setSelectedTime] = useState(
    format12Hour(service.startTime || ""),
  );
  const [selectedDate, setSelectedDate] = useState(service.serviceDate || "");

  useEffect(() => {
    if (service.technician_id && service.technician_id !== staffId) {
      setStaffId(service.technician_id);
    }
  }, [service.technician_id]);

  // ---------- Data from API ----------
  const [groomingServices, setGroomingServices] = useState([]);
  const [availableStaff, setAvailableStaff] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);
  const [businessHours, setBusinessHours] = useState(null);
  const [selectedServiceDetails, setSelectedServiceDetails] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const breakdownRef = useRef(null);
  const lastPriceParamsRef = useRef("");
  let serviceStatus = "";

  switch (String(service.status || "").toLowerCase().trim()) {
    case "cancelled":
    case "canceled":
    case "no show":
      serviceStatus = "Cancelled";
      break;

    case "started":
    case "checkin":
    case "check_in":
    case "in_progress":
      serviceStatus = "Started";
      break;

    case "completed":
    case "complete":
      serviceStatus = "Completed";
      break;

    case "closed":
      serviceStatus = "Closed";
      break;

    case "checkout":
    case "checked_out":
      serviceStatus = "Checked Out";
      break;

    case "confirmed":
      serviceStatus = service.isqueue === 0 ? "Scheduled" : "Queue";
      break;

    case "booked":
      serviceStatus = "Booked";
      break;

    default:
      serviceStatus = service.status
        ? String(service.status).replace(/_/g, " ")
        : "";
      break;
  }

  // 🔥 Add guard ref
  const isInitialMount = useRef(true);

  const fmt = (v) => (Number(v) || 0).toFixed(0);

  // ---------- Helper functions ----------
  const getDurationInMinutes = (durationData, petSize) => {
    if (!durationData) return 0;
    let targetDuration = null;
    if (durationData.duration_based_on === "flat") {
      targetDuration = durationData.flat;
    } else {
      const sizeKey = petSize?.toLowerCase() || "flat";
      const validSizes = ["small", "medium", "large", "giant"];
      const selectedSize = validSizes.includes(sizeKey) ? sizeKey : "flat";
      targetDuration = durationData[selectedSize] || durationData.flat;
    }
    if (!targetDuration) return 0;
    const { hour = 0, minutes = 0 } = targetDuration;
    return hour * 60 + minutes;
  };

  const formatDuration = (durationData, petSize) => {
    if (!durationData) return "";
    let targetDuration = null;
    if (durationData.duration_based_on === "flat") {
      targetDuration = durationData.flat;
    } else {
      const sizeKey = petSize?.toLowerCase() || "flat";
      const validSizes = ["small", "medium", "large", "giant"];
      const selectedSize = validSizes.includes(sizeKey) ? sizeKey : "flat";
      targetDuration = durationData[selectedSize] || durationData.flat;
    }
    if (!targetDuration) return "";
    const { hour = 0, minutes = 0 } = targetDuration;
    const parts = [];
    if (hour > 0) parts.push(`${hour} Hour${hour > 1 ? "s" : ""}`);
    if (minutes > 0) parts.push(`${minutes} Minute${minutes > 1 ? "s" : ""}`);
    return parts.join(" ");
  };

  const getTimeRange = (startTime12h, durationMinutes) => {
    if (!startTime12h || !durationMinutes) return "";
    const match = startTime12h.match(/(\d{1,2}):(\d{2})\s*(am|pm)/i);
    if (!match) return "";
    let [_, hour, minute, period] = match;
    hour = parseInt(hour, 10);
    minute = parseInt(minute, 10);
    period = period.toLowerCase();
    if (period === "pm" && hour !== 12) hour += 12;
    if (period === "am" && hour === 12) hour = 0;
    const startDate = new Date();
    startDate.setHours(hour, minute, 0, 0);
    const endDate = new Date(startDate.getTime() + durationMinutes * 60000);
    const format = (d) => {
      const h = d.getHours().toString().padStart(2, "0");
      const m = d.getMinutes().toString().padStart(2, "0");
      return `${h}:${m}`;
    };
    return `${format(startDate)}-${format(endDate)}`;
  };

  const generateTimeSlots = (businessHours, appointmentDate, interval = 15) => {
    if (!businessHours?.timeSlots?.length) return [];
    const slots = [];
    const today = new Date();
    const isToday =
      appointmentDate &&
      new Date(appointmentDate).toDateString() === today.toDateString();
    const roundedNow =
      Math.ceil((today.getHours() * 60 + today.getMinutes()) / interval) *
      interval;
    businessHours.timeSlots.forEach((range) => {
      let current = new Date(`2000-01-01 ${range.start}`);
      const end = new Date(`2000-01-01 ${range.end}`);
      while (current < end) {
        const slotMinutes = current.getHours() * 60 + current.getMinutes();
        // if (!isToday || slotMinutes >= roundedNow) {
        slots.push(
          current
            .toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            })
            .replace(/\b(am|pm)\b/i, (m) => m.toUpperCase()),
        );
        // }
        current = new Date(current.getTime() + interval * 60000);
      }
    });
    return slots;
  };

  const timeSlots = generateTimeSlots(businessHours, selectedDate);

  const formattedDate = service.serviceDate
    ? new Date(service.serviceDate).toLocaleDateString("en-IN", {
        month: "numeric",
        day: "numeric",
        year: "numeric",
      })
    : "Not set";

  const displayDate = service.serviceDate
    ? new Date(service.serviceDate).toLocaleDateString("en-IN", {
        month: "numeric",
        day: "numeric",
        year: "numeric",
      })
    : service.checkin
      ? new Date(service.checkin).toLocaleDateString("en-IN", {
          month: "numeric",
          day: "numeric",
          year: "numeric",
        })
      : "—";

  // ---------- Fetch service details (conditionally call onUpdate) ----------
  useEffect(() => {
    const fetchServiceDetails = async () => {
      if (!serviceId) {
        setSelectedServiceDetails(null);
        return;
      }
      try {
        const res = await httpService.get(
          `/vendorservice/getvendorservice/${serviceId}`,
        );
        setSelectedServiceDetails(res.data);
        const updatedService = {
          ...service,
          selectedServiceId: serviceId,
          service_id: serviceId,
          serviceName: res.data?.name || "",
          duration: formatDuration(res.data?.duration, pet?.size || ""),
          service_type_name:
            res.data?.service_category?.service_type_name || "grooming",
        };
        // ⏭️ Only call onUpdate if not initial mount
        if (!isInitialMount.current) {
          onUpdate(updatedService);
        }
      } catch (err) {
        console.error("Error fetching service details:", err);
        toast.error("Failed to load service details");
      }
    };
    fetchServiceDetails();
  }, [serviceId]);

  // ---------- Fetch grooming services ----------
  useEffect(() => {
    const fetchGroomingServices = async () => {
      try {
        const params = new URLSearchParams({
          status: "active",
          petType: pet.petType || "",
        });
        const res = await httpService.get(
          `/vendorservice/getvendorservices?${params.toString()}`,
        );
        setGroomingServices(res?.data || []);
        if (serviceId && !service.serviceName) {
          const foundService = res?.data?.find((s) => s._id === serviceId);
          if (foundService) {
            const updatedService = {
              ...service,
              serviceName: foundService.name,
              service_id: serviceId,
            };
            // ⏭️ Only call onUpdate if not initial mount
            if (!isInitialMount.current) {
              onUpdate(updatedService);
            }
          }
        }
      } catch (err) {
        console.error("Error fetching grooming services:", err);
        toast.error("Failed to load grooming services");
      }
    };
    fetchGroomingServices();
  }, [pet.petType]);

  // ---------- Fetch available staff ----------
  useEffect(() => {
    const fetchAvailableStaff = async () => {
      if (!serviceId || !selectedDate || !selectedTime) {
        setAvailableStaff([]);
        return;
      }
      setLoadingStaff(true);
      try {
        const duration = selectedServiceDetails?.duration;
        const durationMinutes = getDurationInMinutes(duration, pet?.size || "");
        const timeRange = getTimeRange(selectedTime, durationMinutes);
        if (!timeRange) {
          setAvailableStaff([]);
          return;
        }

        const payload = {
          date: selectedDate,
          timeSlots: timeRange,
          serviceCategoryTypeId: selectedServiceDetails?.service_category?._id,
          appointmentId: service?.ref_id || null,
        };
        const res = await httpService.post(
          "/employees/getAvailableVendorEmployees",
          payload,
        );

        let filteredStaff = res.data.filter(
          (staff) => staff.is_available !== false,
        );

        if (staffId && !filteredStaff.some((s) => s._id === staffId)) {
          filteredStaff = [
            ...filteredStaff,
            {
              _id: staffId,
              name: service.assignedStaff || "Assigned Staff",
              is_available: true,
            },
          ];
        }

        setAvailableStaff(filteredStaff);
      } catch (err) {
        console.error("Error fetching available staff:", err);
        toast.error("Failed to load staff");
      } finally {
        setLoadingStaff(false);
      }
    };
    fetchAvailableStaff();
  }, [selectedDate, selectedTime, serviceId, selectedServiceDetails]);

  // ---------- Fetch business hours ----------
  useEffect(() => {
    if (selectedDate && serviceId) {
      httpService
        .get("/groominghours/getgroomingbusinesshours")
        .then((res) => setBusinessHours(res?.data || res))
        .catch(() => toast.error("Failed to load business hours"));
    }
  }, [selectedDate, serviceId]);

  // ---------- Click outside to close breakdown ----------
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        breakdownRef.current &&
        !breakdownRef.current.contains(event.target)
      ) {
        setShowBreakdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ---------- Price calculation API ----------
  useEffect(() => {
    // 🚫 Skip entire price calculation on initial mount
    if (isInitialMount.current) return;

    if (!clientId || !pet?.id || !serviceId) return;

    const paramsKey = `${clientId}_${pet.id}_${serviceId}`;
    if (lastPriceParamsRef.current === paramsKey) return;
    lastPriceParamsRef.current = paramsKey;

    setPriceLoading(true);

    const calculatePrice = async () => {
      try {
        const payload = {
          client_id: clientId,
          pet_id: pet.id,
          vendor_service_id: serviceId,
        };
        const response = await httpService.post(
          "/vendorbooking/getPriceCalculation?service_type=grooming",
          payload,
        );

        let salePrice = 0,
          taxAmount = 0,
          taxPercentage = 0,
          discountAmount = 0,
          surchargeDetails = [];

        if (typeof response.data === "number") {
          salePrice = response.data;
        } else if (response.data) {
          salePrice = response.data.sale_price || response.data.price || 0;
          taxAmount =
            response.data.discounted_tax || response.data.taxAmount || 0;
          taxPercentage = response.data.tax_percentage || 0;
          discountAmount = response.data.discount_amount || 0;
          surchargeDetails = response.data.surchargeDetails || [];
        }

        const finalPrice =
          response.data?.discount_percentage === 100 ? 0 : salePrice;

        const updatedService = {
          ...service,
          serviceId,
          serviceName:
            service.serviceName ||
            groomingServices.find((s) => s._id === serviceId)?.name ||
            "",
          price: finalPrice + taxAmount,
          unit_price: salePrice,
          tax_percentage: taxPercentage,
          gst_amount: taxAmount,
          discount_amount: discountAmount,
          price_surcharge: surchargeDetails,
          sale_price: salePrice,
          mrp_price:
            response.data?.mrp_price ||
            response.data?.price ||
            finalPrice + taxAmount + discountAmount,
          amount_before_discount:
            response.data?.mrp_price ||
            response.data?.price ||
            finalPrice + taxAmount + discountAmount,
          originalPrice: service.originalPrice ?? service.price,
          service_type_name: "grooming",
        };
        onUpdate(updatedService);
      } catch (error) {
        console.error("Error calculating price:", error);
        const fallbackService = groomingServices.find(
          (s) => s._id === serviceId,
        );
        const fallbackPrice =
          Number(fallbackService?.mrp_price) ||
          Number(fallbackService?.basic_price) ||
          service.price ||
          0;

        const updatedService = {
          ...service,
          serviceId,
          serviceName: service.serviceName || fallbackService?.name || "",
          price: fallbackPrice,
          unit_price: fallbackPrice,
          tax_percentage: 0,
          gst_amount: 0,
          discount_amount: 0,
          price_surcharge: [],
          sale_price: 0,
          originalPrice: service.originalPrice ?? service.price,
          service_type_name: "grooming",
        };
        onUpdate(updatedService);
        toast.error("Failed to calculate price");
      } finally {
        setPriceLoading(false);
      }
    };

    calculatePrice();
  }, [clientId, pet?.id, serviceId, groomingServices]);

  // 🔥 Set isInitialMount to false after initial render (must be after guarded effects)
  useEffect(() => {
    isInitialMount.current = false;
  }, []);

  return (
    <CheckoutItemTable
      serviceType="grooming"
      service={service}
      pet={pet}
      getPetIcon={getPetIcon}
      serviceStatus={serviceStatus}
      isOrderClosed={isOrderClosed}
      priceLoading={priceLoading}
      clientId={clientId}
      serviceCategoryId={selectedServiceDetails?.category_type_id}
      petTypeId={pet.pet_type_id}
      serviceTypeName="Grooming"
      surcharges={service.price_surcharge || []}
      onUpdateService={onUpdate}
      onViewActivity={onViewActivity}
      renderServiceSelector={() => (
        <select
          value={service.selectedServiceId || serviceId || ""}
          disabled={isOrderClosed}
          onChange={(e) => {
            const newServiceId = e.target.value;
            setServiceId(newServiceId);
            onUpdate({
              ...service,
              selectedServiceId: newServiceId,
              service_id: newServiceId,
            });
          }}
          className="text-sm border border-gray-300 rounded-lg bg-white px-2 py-1 font-medium text-gray-800 focus:ring-1 focus:ring-indigo-500"
        >
          {groomingServices.map((opt) => (
            <option key={opt._id} value={opt._id}>
              {opt.name} (₹
              {Number(opt.mrp_price) || Number(opt.basic_price || 0)})
            </option>
          ))}
        </select>
      )}
      renderMetaInfo={() => (
        <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
          {displayDate}
        </span>
      )}
    />
  );
};

export default GroomingCheckoutCard;
